/* ---------------------------------------------------------------------------
   Cookie consent — one optional category (analytics). Strictly-necessary
   storage (session, preferences, this choice) needs no consent.

   • The choice lives in localStorage under CONSENT_KEY with a version, so a
     future change to what we collect can re-ask.
   • PostHog is initialised with opt_out_capturing_by_default (public/index.html)
     — nothing is captured and no analytics cookie is written until the visitor
     accepts. applyConsent() flips PostHog on/off to mirror the stored choice.
   • Tenant-branded owner surfaces never opt in, whatever the stored choice:
     analytics belongs to the property manager's own site, not to Listed.
--------------------------------------------------------------------------- */

export const CONSENT_KEY = "listed_consent";
export const CONSENT_VERSION = 1;
const EVENT = "listed:consent:change";

/** Routes where Listed must stay silent — the owner is the property manager's visitor, not ours. */
const OWNER_SURFACES = [/^\/start\//, /^\/w\//, /^\/offer\//, /^\/sign\//, /^\/onboard\//];
export const isOwnerSurface = (pathname) => OWNER_SURFACES.some((re) => re.test(pathname || ""));

export function getConsent() {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setConsent({ analytics }) {
  const value = { version: CONSENT_VERSION, analytics: !!analytics, at: new Date().toISOString() };
  try { window.localStorage.setItem(CONSENT_KEY, JSON.stringify(value)); } catch { /* private mode — the choice lives for this page view only */ }
  applyConsent(value, window.location.pathname);
  try { window.dispatchEvent(new CustomEvent(EVENT, { detail: value })); } catch { /* noop */ }
  return value;
}

export function onConsentChange(fn) {
  const h = (e) => fn(e.detail);
  window.addEventListener(EVENT, h);
  return () => window.removeEventListener(EVENT, h);
}

/** Mirror the stored choice into PostHog. Safe to call before the SDK has loaded (stub queues calls). */
export function applyConsent(consent = getConsent(), pathname = window.location.pathname) {
  const ph = typeof window !== "undefined" ? window.posthog : null;
  if (!ph) return;
  const allowed = !!consent?.analytics && !isOwnerSurface(pathname);
  try {
    if (allowed) {
      if (typeof ph.opt_in_capturing === "function") ph.opt_in_capturing();
      // Session replay stays off: "analytics" means page views and interactions, not recordings.
    } else {
      if (typeof ph.opt_out_capturing === "function") ph.opt_out_capturing();
    }
  } catch { /* analytics must never break the app */ }
}

/** Lightweight product event — silently dropped without consent. */
export function track(event, props) {
  const c = getConsent();
  if (!c?.analytics || isOwnerSurface(window.location.pathname)) return;
  try { window.posthog?.capture?.(event, props); } catch { /* noop */ }
}
